/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * System Health Hooks - React Query for System Health Dashboard
 * ============================================================================
 *
 * Custom hooks for system health data fetching using React Query.
 *
 * ============================================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  systemHealthService,
  SystemHealth,
  HealthHistory,
  SlowRequest,
} from '@/services/superadmin/system-health.service';
import { ApiError, PagedResponse } from '@/services/api/types';

// Query keys for cache management
export const systemHealthKeys = {
  all: ['system-health'] as const,
  current: () => [...systemHealthKeys.all, 'current'] as const,
  history: (hours: number) => [...systemHealthKeys.all, 'history', { hours }] as const,
  slowRequests: (page: number, size: number, threshold?: number) =>
    [...systemHealthKeys.all, 'slow-requests', { page, size, threshold }] as const,
};

/**
 * Hook to fetch current system health status
 */
export function useSystemHealth() {
  return useQuery<SystemHealth, ApiError>({
    queryKey: systemHealthKeys.current(),
    queryFn: systemHealthService.getCurrentHealth,
    staleTime: 10 * 1000, // 10 seconds
    refetchInterval: 30 * 1000, // Refetch every 30 seconds
  });
}

/**
 * Hook to fetch health history
 */
export function useHealthHistory(hours: number = 24) {
  return useQuery<HealthHistory, ApiError>({
    queryKey: systemHealthKeys.history(hours),
    queryFn: () => systemHealthService.getHealthHistory(hours),
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook to fetch slow request logs
 */
export function useSlowRequests(
  page: number = 0,
  size: number = 20,
  thresholdMs?: number
) {
  return useQuery<PagedResponse<SlowRequest>, ApiError>({
    queryKey: systemHealthKeys.slowRequests(page, size, thresholdMs),
    queryFn: () => systemHealthService.getSlowRequests(page, size, thresholdMs),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to manually trigger a health snapshot
 */
export function useTakeSnapshot() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, void>({
    mutationFn: systemHealthService.takeSnapshot,
    onSuccess: () => {
      // Invalidate health queries to refresh data
      queryClient.invalidateQueries({ queryKey: systemHealthKeys.all });
    },
  });
}
